/**
 * HustleMeetProfilePage.jsx — one questionnaire, save-as-you-go.
 *
 * Backing endpoints:
 *   GET   /api/hustlemeet-profile/questions   — spec (static)
 *   GET   /api/hustlemeet-profile/mine        — my profile + completion
 *   PATCH /api/hustlemeet-profile/mine        — partial update (autosaves)
 *
 * Sections rendered as accordion cards. Each section autosaves 800ms
 * after the last edit so the member never fears losing progress.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ChevronDown, ChevronUp, Save, Sparkles,
         Utensils, Heart, MapPin, User, Users, Compass,
         CalendarDays, Star, Mic, Bookmark, MessageCircle } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const authHeader = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/* ── Section shell ───────────────────────────────────────────────── */
function Section({ id, icon: Icon, title, subtitle, done, open, onToggle, children }) {
  return (
    <div
      data-testid={`profile-section-${id}`}
      className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 overflow-hidden mb-4"
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-5 text-left hover:bg-stone-50 dark:hover:bg-slate-900 transition"
        data-testid={`profile-section-toggle-${id}`}
      >
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${done
          ? "bg-emerald-500/10 text-emerald-500" : "bg-amber-500/10 text-amber-500"}`}>
          {done ? <CheckCircle2 size={20}/> : <Icon size={20}/>}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-black">{title}</p>
          {subtitle && <p className="text-xs text-stone-500 mt-0.5">{subtitle}</p>}
        </div>
        {open ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-stone-200 dark:border-stone-800">
          {children}
        </div>
      )}
    </div>
  );
}

function Likert({ value, onChange, testId }) {
  return (
    <div className="flex items-center gap-1.5" data-testid={testId}>
      {[1,2,3,4,5,6,7].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`w-9 h-9 rounded-full text-xs font-black transition ${
            value === n
              ? "bg-amber-500 text-slate-950"
              : "bg-stone-100 dark:bg-slate-800 text-stone-500 hover:bg-amber-500/20"
          }`}
        >{n}</button>
      ))}
    </div>
  );
}

function Chip({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3.5 h-9 rounded-full text-xs font-bold transition border ${
        active
          ? "bg-amber-500 border-amber-500 text-slate-950"
          : "bg-white dark:bg-slate-950 border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-300 hover:border-amber-400"
      }`}
    >{label}</button>
  );
}

/* ── Sprint-2 · "Your HustleMeet" panel ───────────────────────────── */
function YourMeetPanel({ rsvps, hosts, matches, onToggleSave, onIcebreaker }) {
  // Skeleton while the 3 requests are in flight.
  if (rsvps === null && hosts === null && matches === null) return null;
  const hasAny = (rsvps && rsvps.length)
                  || (hosts && hosts.length)
                  || (matches && matches.length);
  return (
    <section
      data-testid="your-meet-panel"
      className="mb-8 grid grid-cols-1 sm:grid-cols-3 gap-4"
    >
      {/* RSVPs */}
      <div
        data-testid="panel-rsvps"
        className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-4"
      >
        <div className="flex items-center gap-2 text-amber-500">
          <CalendarDays size={16}/>
          <p className="text-[11px] font-black uppercase tracking-widest">Upcoming</p>
        </div>
        <p className="mt-2 text-2xl font-black leading-none" data-testid="rsvps-count">
          {rsvps ? rsvps.length : "…"}
        </p>
        <p className="text-xs text-stone-500 mt-1">
          {rsvps && rsvps.length
            ? rsvps[0].event.title
            : rsvps ? "No RSVPs yet" : "Loading…"}
        </p>
        {rsvps && rsvps.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {rsvps.slice(0, 3).map((r) => (
              <a
                key={r.ticket_id}
                data-testid={`rsvp-${r.ticket_id}`}
                href={`/hustle-events/${r.event.slug || r.event.id}`}
                className="block text-xs text-stone-700 dark:text-stone-300 hover:text-amber-500 truncate"
              >
                <span className="text-amber-500 font-black">
                  {new Date(r.event.starts_at).toLocaleDateString(undefined,
                    { month: "short", day: "numeric" })}
                </span>{" · "}
                {r.event.title}
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Hosts */}
      <div
        data-testid="panel-hosts"
        className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-4"
      >
        <div className="flex items-center gap-2 text-amber-500">
          <Mic size={16}/>
          <p className="text-[11px] font-black uppercase tracking-widest">Hosts</p>
        </div>
        <p className="mt-2 text-2xl font-black leading-none" data-testid="hosts-count">
          {hosts ? hosts.length : "…"}
        </p>
        <p className="text-xs text-stone-500 mt-1">
          {hosts && hosts.length
            ? "Groups & hosts you follow"
            : hosts ? "Join a Circle to follow hosts" : "Loading…"}
        </p>
        {hosts && hosts.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {hosts.slice(0, 6).map((h, i) => (
              <span
                key={h.id || h.name || i}
                data-testid={`host-chip-${i}`}
                className="text-[11px] px-2 py-1 rounded-full bg-stone-100 dark:bg-slate-900 text-stone-600 dark:text-stone-300 truncate max-w-[9rem]"
                title={h.host_name || h.name}
              >
                {h.host_name || h.name}
                {h.verified && (
                  <span className="ml-1 text-amber-500">✓</span>
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Matches */}
      <div
        data-testid="panel-matches"
        className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-4"
      >
        <div className="flex items-center gap-2 text-amber-500">
          <Star size={16}/>
          <p className="text-[11px] font-black uppercase tracking-widest">Matches</p>
        </div>
        <p className="mt-2 text-2xl font-black leading-none" data-testid="matches-count">
          {matches ? matches.length : "…"}
        </p>
        <p className="text-xs text-stone-500 mt-1">
          {matches && matches.length
            ? "People you're paired with at upcoming events"
            : matches ? "Fill your profile to unlock matches" : "Loading…"}
        </p>
        {matches && matches.length > 0 && (
          <div className="mt-3 space-y-2.5">
            {matches.slice(0, 3).map((m, i) => (
              <div
                key={i}
                data-testid={`match-row-${i}`}
                className="text-xs"
              >
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center text-[10px] font-black">
                    {m.peer.first_name?.[0] || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-bold">{m.peer.first_name}</p>
                    <p className="text-[10px] text-stone-500 truncate">
                      {m.event.title}
                    </p>
                  </div>
                  <span className="text-[10px] font-black text-amber-500 tabular-nums cursor-help"
                        data-testid={`match-score-${i}`}
                        title={
                          (m.shared_interests && m.shared_interests.length)
                            ? `${Math.round(m.score * 100)}% match — shared: ${m.shared_interests.join(", ")}`
                            : `${Math.round(m.score * 100)}% compatibility from Big-5 alignment, life-stage overlap and location proximity. Higher = more common ground.`
                        }>
                    {Math.round(m.score * 100)}%
                  </span>
                </div>
                {/* Sprint 3 — Save + Icebreaker actions */}
                {m.peer_id && (
                  <div
                    data-testid={`match-actions-${i}`}
                    className="mt-1.5 pl-8 flex items-center gap-3"
                  >
                    <button
                      type="button"
                      onClick={() => onToggleSave && onToggleSave(m, i)}
                      data-testid={`match-save-${i}`}
                      className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest transition ${
                        m.saved
                          ? "text-amber-500"
                          : "text-stone-400 hover:text-amber-500"
                      }`}
                      title={m.saved ? "Remove from saved matches" : "Save this match"}
                    >
                      <Bookmark
                        size={11}
                        fill={m.saved ? "currentColor" : "none"}
                      />
                      {m.saved ? "Saved" : "Save"}
                    </button>
                    <button
                      type="button"
                      onClick={() => onIcebreaker && onIcebreaker(m, i)}
                      data-testid={`match-icebreaker-${i}`}
                      className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-stone-400 hover:text-amber-500 transition"
                      title={`Send a warm intro to ${m.peer.first_name}`}
                    >
                      <MessageCircle size={11}/>
                      Say hi
                    </button>
                  </div>
                )}
                {/* Why we matched you — mini 3-bar chart */}
                {Array.isArray(m.dimensions) && m.dimensions.length > 0 && (
                  <div
                    data-testid={`match-dims-${i}`}
                    className="mt-1.5 pl-8 space-y-1"
                  >
                    {m.dimensions.slice(0, 3).map((d, di) => (
                      <div
                        key={di}
                        data-testid={`match-dim-${i}-${di}`}
                        className="flex items-center gap-2"
                        title={
                          Array.isArray(d.detail) && d.detail.length
                            ? `${d.label}: ${d.detail.join(", ")}`
                            : d.label
                        }
                      >
                        <span className="text-[9px] uppercase tracking-widest text-stone-500 w-[62px] truncate">
                          {d.label}
                        </span>
                        <div className="flex-1 h-1 rounded-full bg-stone-100 dark:bg-slate-900 overflow-hidden">
                          <div
                            className="h-full bg-amber-500 rounded-full transition-all"
                            style={{ width: `${Math.max(4, Math.min(100, d.value))}%` }}
                          />
                        </div>
                        <span className="text-[9px] font-black text-stone-500 tabular-nums w-[26px] text-right">
                          {d.value}%
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {!hasAny && (
        <p
          data-testid="panel-empty"
          className="sm:col-span-3 text-xs text-stone-500 mt-1"
        >
          Complete your profile below to unlock personalised meetup invites
          and matched attendees.
        </p>
      )}
    </section>
  );
}

/* ── Page ────────────────────────────────────────────────────────── */
export default function HustleMeetProfilePage() {
  const [spec,    setSpec]    = useState(null);
  const [profile, setProfile] = useState(null);
  const [open,    setOpen]    = useState({ dietary: true });
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState("");
  const [rsvps,   setRsvps]   = useState(null);
  const [hosts,   setHosts]   = useState(null);
  const [matches, setMatches] = useState(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "HustleMeet · Profile & Preferences";
    (async () => {
      try {
        const [s, p] = await Promise.all([
          axios.get(`${API}/hustlemeet-profile/questions`),
          axios.get(`${API}/hustlemeet-profile/mine`,
            { headers: authHeader(), withCredentials: true }),
        ]);
        setSpec(s.data);
        setProfile(p.data);
        // Sprint-2 side panels — load in parallel, non-blocking. If any
        // 404 (e.g. legacy backend) we just render nothing.
        const H = { headers: authHeader(), withCredentials: true };
        Promise.allSettled([
          axios.get(`${API}/hustlemeet-profile/mine/rsvps`,   H),
          axios.get(`${API}/hustlemeet-profile/mine/hosts`,   H),
          axios.get(`${API}/hustlemeet-profile/mine/matches`, H),
        ]).then(([r, h, m]) => {
          setRsvps(r.status === "fulfilled" ? r.value.data.rsvps || []   : []);
          setHosts(h.status === "fulfilled" ? h.value.data.hosts || []   : []);
          setMatches(m.status === "fulfilled" ? m.value.data.matches || [] : []);
        });
      } catch (e) {
        // Unauthenticated visitors trying the Matchmaker quiz would
        // otherwise see a dead "Please sign in" wall — funnel drop-off.
        // Bounce them to register with returnTo so they land back here
        // right after finishing signup.
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          const returnTo = encodeURIComponent("/meet/profile");
          navigate(`/register?returnTo=${returnTo}&ref=hustlemeet-matchmaker`,
                   { replace: true });
          return;
        }
        setErr(e?.response?.data?.detail || e.message);
      }
    })();
  }, [navigate]);

  const patch = useCallback(async (delta) => {
    const merged = { ...(profile || {}), ...delta };
    setProfile(merged);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        const { data } = await axios.patch(
          `${API}/hustlemeet-profile/mine`, delta,
          { headers: authHeader(), withCredentials: true });
        setProfile(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Save failed");
      } finally {
        setSaving(false);
      }
    }, 800);
  }, [profile]);

  const toggle = (k) => setOpen(o => ({ ...o, [k]: !o[k] }));

  // ── Sprint 3 · Save match + Icebreaker handlers ────────────────
  const toggleSaveMatch = useCallback(async (m, i) => {
    if (!m || !m.peer_id) return;
    const url = `${API}/hustlemeet-profile/mine/matches/${encodeURIComponent(m.peer_id)}/save`;
    const isSaved = !!m.saved;
    // Optimistic UI
    setMatches((rows) => {
      if (!Array.isArray(rows)) return rows;
      const next = [...rows];
      if (next[i]) next[i] = { ...next[i], saved: !isSaved };
      return next;
    });
    try {
      const H = { headers: authHeader(), withCredentials: true };
      if (isSaved) await axios.delete(url, H);
      else         await axios.post(url, {}, H);
      toast.success(isSaved ? "Removed from saved" : "Match saved");
    } catch (e) {
      // Roll back on error
      setMatches((rows) => {
        if (!Array.isArray(rows)) return rows;
        const next = [...rows];
        if (next[i]) next[i] = { ...next[i], saved: isSaved };
        return next;
      });
      toast.error(e?.response?.data?.detail || "Could not update save");
    }
  }, []);

  const sendIcebreaker = useCallback(async (m) => {
    if (!m || !m.peer_id) return;
    try {
      const H = { headers: authHeader(), withCredentials: true };
      const { data } = await axios.post(
        `${API}/hustlemeet-profile/mine/matches/${encodeURIComponent(m.peer_id)}/icebreaker`,
        {}, H);
      if (data && data.ok) {
        toast.success(`Said hi to ${data.peer_first_name || m.peer.first_name}`);
      } else {
        toast.info("Icebreaker queued");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not send icebreaker";
      toast.error(detail);
    }
  }, []);

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0a0908] flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="profile-error">
          <p className="text-lg font-black">Please sign in</p>
          <p className="mt-2 text-stone-500 text-sm">{err}</p>
        </div>
      </div>
    );
  }

  if (!spec || !profile) {
    return (
      <div
        data-testid="profile-loading"
        className="min-h-screen bg-slate-50 dark:bg-[#0a0908] flex items-center justify-center text-stone-400"
      >Loading…</div>
    );
  }

  const dietary       = profile.dietary || {};
  const big5          = profile.big5 || {};
  const values        = profile.values || {};
  const lifeStage     = profile.life_stage || {};
  const interests     = profile.interests || [];
  const logistics     = profile.logistics || {};
  const partnerPrefs  = profile.partner_prefs || {};

  // Section completion booleans
  const doneDietary   = Object.keys(dietary).length > 0;
  const doneBig5      = Object.keys(big5).length === 5;
  const doneValues    = Object.keys(values).length >= 4;
  const doneLife      = Object.keys(lifeStage).length >= 3;
  const doneInterests = interests.length >= 5;
  const doneLogistics = !!logistics.home_suburb;
  const donePartner   = profile.dating_optin
    && partnerPrefs.age_min && partnerPrefs.age_max
    && partnerPrefs.gender_pref;

  return (
    <div
      data-testid="hustlemeet-profile-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: "HustleMeet · Profile", to: "/meet/profile" }}
        showBookCta={false}
        showCart={false}
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        {/* Sprint-2 — Your HustleMeet at a glance */}
        <YourMeetPanel
          rsvps={rsvps}
          hosts={hosts}
          matches={matches}
          onToggleSave={toggleSaveMatch}
          onIcebreaker={sendIcebreaker}
        />

        {/* Header + completion */}
        <header className="mb-8">
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            HustleMeet
          </p>
          <h1 className="mt-2 text-3xl md:text-4xl font-black leading-tight">
            Your matching profile
          </h1>
          <p className="mt-2 text-sm text-stone-500 max-w-xl">
            Fill it once — feeds every HustleMeet event, Circle and Matchmaker
            table. Saves automatically as you go.
          </p>
          <div className="mt-5 rounded-2xl border border-amber-300 dark:border-amber-800 bg-gradient-to-br from-amber-400/10 to-orange-500/10 p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-amber-500 font-black">
                  Profile completion
                </p>
                <p className="mt-1 text-3xl font-black text-amber-500"
                  data-testid="profile-completion-pct">
                  {profile.completion_pct}%
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-stone-500">
                  Matchmaker ready?
                </p>
                <p className={`text-sm font-black ${profile.dating_ready
                  ? "text-emerald-500" : "text-stone-400"}`}
                  data-testid="profile-dating-ready">
                  {profile.dating_ready ? "Yes" : "Not yet"}
                </p>
              </div>
            </div>
            <div className="mt-3 h-2 rounded-full bg-white/60 dark:bg-slate-900 overflow-hidden">
              <div
                className="h-full bg-amber-500 transition-all"
                style={{ width: `${profile.completion_pct}%` }}
              />
            </div>
            {saving && (
              <p className="mt-3 text-[11px] text-amber-500 font-bold inline-flex items-center gap-1"
                data-testid="profile-saving">
                <Save size={11} className="animate-pulse"/> Saving…
              </p>
            )}
          </div>
        </header>

        {/* Dietary (mandatory for Matchmaker) */}
        <Section id="dietary" icon={Utensils} title="Dietary requirements"
          subtitle="Mandatory before joining Matchmaker · shared with the venue 7 days before every dinner"
          done={doneDietary} open={!!open.dietary} onToggle={() => toggle("dietary")}>
          <div className="flex flex-wrap gap-2 mt-4">
            {[
              ["is_vegetarian",  "Vegetarian"],
              ["is_vegan",       "Vegan"],
              ["is_pescatarian", "Pescatarian"],
              ["halal",          "Halal"],
              ["kosher",         "Kosher"],
              ["gluten_free",    "Gluten-free"],
              ["dairy_free",     "Dairy-free"],
              ["alcohol_free",   "Alcohol-free"],
            ].map(([k, lbl]) => (
              <Chip
                key={k}
                label={lbl}
                active={!!dietary[k]}
                onClick={() => patch({ dietary: { ...dietary, [k]: !dietary[k] } })}
                testId={`chip-dietary-${k}`}
              />
            ))}
          </div>
          <label className="block mt-4 text-[11px] uppercase tracking-widest text-stone-500 font-bold">
            Allergies (comma-separated)
          </label>
          <input
            data-testid="input-allergies"
            className="mt-1 w-full h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
            defaultValue={(dietary.allergies || []).join(", ")}
            onBlur={(e) => patch({ dietary: { ...dietary,
              allergies: e.target.value.split(",").map(s => s.trim()).filter(Boolean) } })}
            placeholder="peanut, shellfish, sesame…"
          />
          <label className="block mt-4 text-[11px] uppercase tracking-widest text-stone-500 font-bold">
            Notes for chef (optional)
          </label>
          <textarea
            data-testid="input-dietary-notes"
            rows={2}
            className="mt-1 w-full rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
            defaultValue={dietary.notes || ""}
            onBlur={(e) => patch({ dietary: { ...dietary, notes: e.target.value } })}
            placeholder="Severity of any allergy, EpiPen carried, anything the kitchen should know."
          />
        </Section>

        {/* Big-5 personality */}
        <Section id="big5" icon={Sparkles} title="How you tick"
          subtitle="10 quick agree/disagree — takes 90 seconds"
          done={doneBig5} open={!!open.big5} onToggle={() => toggle("big5")}>
          <ul className="mt-4 divide-y divide-stone-200 dark:divide-stone-800">
            {spec.big5.map((q, i) => {
              // Two questions per dimension; final value = avg per dim.
              const key = `big5_${i}`;
              const dim = q.dim;
              const stored = big5[`${dim}_${q.reverse ? "r" : "f"}`];
              return (
                <li key={key} className="py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                  <p className="text-sm flex-1">{q.prompt}</p>
                  <Likert
                    value={stored}
                    onChange={(v) => {
                      // Store per-question then compute the dim average.
                      const nxt = { ...big5, [`${dim}_${q.reverse ? "r" : "f"}`]: v };
                      const f = nxt[`${dim}_f`], rw = nxt[`${dim}_r`];
                      let avg = null;
                      if (f && rw) avg = Math.round((f + (8 - rw)) / 2);
                      else if (f) avg = f;
                      else if (rw) avg = 8 - rw;
                      const cleaned = { ...nxt };
                      if (avg !== null) cleaned[dim] = avg;
                      patch({ big5: cleaned });
                    }}
                    testId={`likert-${key}`}
                  />
                </li>
              );
            })}
          </ul>
        </Section>

        {/* Values */}
        <Section id="values" icon={Compass} title="What matters to you"
          subtitle="These are the actual deal-breakers"
          done={doneValues} open={!!open.values} onToggle={() => toggle("values")}>
          <div className="mt-4 space-y-4">
            {spec.values.map((q) => (
              <div key={q.key} data-testid={`values-${q.key}`}>
                <p className="text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
                  {q.label}
                </p>
                <div className="flex flex-wrap gap-2">
                  {q.options.map((opt) => (
                    <Chip
                      key={opt}
                      label={opt.replace(/_/g, " ")}
                      active={values[q.key] === opt}
                      onClick={() => patch({ values: { ...values, [q.key]: opt } })}
                      testId={`chip-value-${q.key}-${opt}`}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Interests */}
        <Section id="interests" icon={Heart} title="Interests"
          subtitle={`Pick up to 5 (${interests.length}/5)`}
          done={doneInterests} open={!!open.interests} onToggle={() => toggle("interests")}>
          <div className="mt-4 flex flex-wrap gap-2">
            {spec.interests.map((it) => {
              const active = interests.includes(it);
              const disabled = !active && interests.length >= 5;
              return (
                <Chip
                  key={it}
                  label={it}
                  active={active}
                  onClick={() => {
                    if (disabled) {
                      toast.info("Pick up to 5 interests");
                      return;
                    }
                    const next = active
                      ? interests.filter(x => x !== it)
                      : [...interests, it];
                    patch({ interests: next });
                  }}
                  testId={`chip-interest-${it.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                />
              );
            })}
          </div>
        </Section>

        {/* Life stage */}
        <Section id="life_stage" icon={User} title="Life stage"
          subtitle="Where you are today"
          done={doneLife} open={!!open.life_stage} onToggle={() => toggle("life_stage")}>
          <div className="mt-4 space-y-4">
            {spec.life_stage.map((q) => (
              <div key={q.key} data-testid={`lifestage-${q.key}`}>
                <p className="text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">{q.label}</p>
                <div className="flex flex-wrap gap-2">
                  {q.options.map((opt) => (
                    <Chip
                      key={opt}
                      label={opt.replace(/_/g, " ")}
                      active={lifeStage[q.key] === opt}
                      onClick={() => patch({ life_stage: { ...lifeStage, [q.key]: opt } })}
                      testId={`chip-life-${q.key}-${opt}`}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Logistics */}
        <Section id="logistics" icon={MapPin} title="Location & availability"
          subtitle="How far you'll travel + which nights suit"
          done={doneLogistics} open={!!open.logistics} onToggle={() => toggle("logistics")}>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-bold">Home suburb</label>
              <input
                data-testid="input-home-suburb"
                className="mt-1 w-full h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
                defaultValue={logistics.home_suburb || ""}
                onBlur={(e) => patch({ logistics: { ...logistics, home_suburb: e.target.value } })}
                placeholder="e.g. South Brisbane"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-bold">State</label>
              <input
                data-testid="input-home-state"
                className="mt-1 w-full h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
                defaultValue={logistics.home_state || ""}
                onBlur={(e) => patch({ logistics: { ...logistics, home_state: e.target.value } })}
                placeholder="QLD"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-bold">Max travel (km)</label>
              <input
                data-testid="input-max-travel-km"
                type="number" min="1" max="1000"
                className="mt-1 w-full h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
                defaultValue={logistics.max_travel_km || 25}
                onBlur={(e) => patch({ logistics: { ...logistics, max_travel_km: Number(e.target.value) || 25 } })}
              />
            </div>
          </div>
          <p className="mt-4 text-[11px] uppercase tracking-widest text-stone-500 font-bold">Availability</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {spec.availability.map((slot) => (
              <Chip
                key={slot}
                label={slot.replace("_", " ")}
                active={(logistics.availability || []).includes(slot)}
                onClick={() => {
                  const cur = logistics.availability || [];
                  const next = cur.includes(slot)
                    ? cur.filter(x => x !== slot)
                    : [...cur, slot];
                  patch({ logistics: { ...logistics, availability: next } });
                }}
                testId={`chip-avail-${slot}`}
              />
            ))}
          </div>
        </Section>

        {/* Dating opt-in + partner preferences */}
        <Section id="dating" icon={Users}
          title={`Dating opt-in ${profile.dating_optin ? "· ON" : "· OFF"}`}
          subtitle="Optional — unlocks Matchmaker with dating filters"
          done={donePartner} open={!!open.dating} onToggle={() => toggle("dating")}>
          <div className="mt-4 space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1"
                data-testid="chk-community-standards"
                checked={!!profile.community_standards_ok}
                onChange={(e) => patch({ community_standards_ok: e.target.checked })}
              />
              <span className="text-sm">
                I&apos;ve read and accept the <a href="/legal/community-standards" target="_blank" rel="noreferrer" className="text-amber-500 underline">HustleSuite Community Standards</a> — respect, honesty, zero tolerance for harassment.
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1"
                data-testid="chk-dating-optin"
                checked={!!profile.dating_optin}
                onChange={(e) => {
                  if (e.target.checked && !profile.community_standards_ok) {
                    toast.error("Accept community standards first.");
                    return;
                  }
                  patch({ dating_optin: e.target.checked });
                }}
              />
              <span className="text-sm">
                Turn on dating filters (Matchmaker table balancing uses your partner preferences)
              </span>
            </label>

            {profile.dating_optin && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-bold">Age min</label>
                  <input
                    data-testid="input-age-min"
                    type="number" min="18" max="99"
                    className="mt-1 w-full h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
                    defaultValue={partnerPrefs.age_min || 30}
                    onBlur={(e) => patch({ partner_prefs: { ...partnerPrefs, age_min: Number(e.target.value) || 30 } })}
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-bold">Age max</label>
                  <input
                    data-testid="input-age-max"
                    type="number" min="18" max="99"
                    className="mt-1 w-full h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
                    defaultValue={partnerPrefs.age_max || 55}
                    onBlur={(e) => patch({ partner_prefs: { ...partnerPrefs, age_max: Number(e.target.value) || 55 } })}
                  />
                </div>
                <div className="sm:col-span-2">
                  <p className="text-[11px] uppercase tracking-widest text-stone-500 font-bold mb-2">Gender preference</p>
                  <div className="flex flex-wrap gap-2">
                    {["male", "female", "any", "nonbinary", "same_as_me"].map((g) => (
                      <Chip
                        key={g}
                        label={g.replace(/_/g, " ")}
                        active={partnerPrefs.gender_pref === g}
                        onClick={() => patch({ partner_prefs: { ...partnerPrefs, gender_pref: g } })}
                        testId={`chip-gender-${g}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </Section>

        <div className="mt-6 flex justify-end">
          <Button
            data-testid="profile-view-hustlemeet"
            asChild
            className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black"
          >
            <a href="/meet">Browse HustleMeet Circles →</a>
          </Button>
        </div>
      </div>

      <SuiteFooter />
    </div>
  );
}
